import { BaseModel } from "./BaseModel";
import { ColumnHeaders, TabNames, Currency } from '../modules/Constants';


export class METoolingModel extends BaseModel {
    constructor() {
        super();
        this.TabName = TabNames.ME_TOOLING;
        this.TabIndex = 4;
        this.DataProcessor = new METoolingProcessor();
    }
}

export class METoolingProcessor {
    ExchangeRates;
    Years;
    Currency;
    Records;
    Headers;

    process(data) {

        this.ExchangeRates = [];
        this.Years = [];
        this.Records = [];
        this.Headers = [];
        this.Currency = data.currency;

        let study = data.study;
        let snapshots = data.snapshots;
        let version = data.version;


        study.exchangeRates.forEach(rate => {
            rate.Item2.Values.forEach(item => {
                this.ExchangeRates.push({
                    currency: rate.Item1,
                    year: new Date(item.Date).getUTCFullYear(),
                    value: item.Value
                });
            });
        });


        let firstSOPYear = Math.min.apply(Math, snapshots.map(snapshot => snapshot.SaleableParts?.map(part => new Date(part?.CustomerSOP?.Value.Fields[0]).getUTCFullYear())).reduce(function (a, b) { return a.concat(b) }, []));
        let firstYearSpend = firstSOPYear - (version?.SpendYearsPriorToSOP?.Value?.Fields[0] ?? 2);
        let lastYearSpend = firstSOPYear + (version?.SpendYearsAfterSOP?.Value?.Fields[0] ?? 0);
        snapshots.forEach(snapshot => {
            snapshot.SaleableParts.forEach(part => {
                let endDate = part.CustomerVolume?.Value?.Fields[0]?.EndDate;
                if (endDate) {
                    let year = new Date(endDate).getUTCFullYear();
                    if (year > lastYearSpend) {
                        lastYearSpend = year;
                    }
                }
            });
        })

        var yearsSpending = Array.from(Array((lastYearSpend - firstYearSpend) + 1), (_, x) => x + firstYearSpend);
        this.Years = yearsSpending;


        //JG - Update Years in case there are additional years outside of Spending Years.
        snapshots.forEach(snapshot => {
            snapshot?.Manufacturing.forEach(manufacturing => {
                manufacturing.Operations.forEach(op => {
                    let spendTiming = op.SpendTiming;

                    let spendTimingYears = spendTiming?.Value?.Fields[0]?.Values;

                    spendTimingYears.forEach(spendTimingYear => {
                        let year = new Date(spendTimingYear.Date).getUTCFullYear();

                        if (this.Years.findIndex(x => x === year) <= -1) {
                            this.Years.push(year);
                        }
                    });
                });
            });
        });


        this.Years.sort();

        snapshots.forEach(snapshot => {
            this.setRecords(snapshot);
        });


        this.configureHeader();
        let result = {
            headers: this.Headers,
            display: this.getDisplay(),
            numerics: this.getNumerics()
        };


        return result;
    }

    setRecords(snapshot) {
        snapshot?.Manufacturing.forEach(manufacturing => {
            manufacturing.Operations.forEach(op => {
                if (op.ManufacturingToolInvestment?.length < 1) {
                    this.Records.push(new METoolingRowModel(this.Years, snapshot, manufacturing, op, null, this.ExchangeRates, this.Currency));
                    return;
                }

                op.ManufacturingToolInvestment.forEach(mfgToolInvestment => {
                    this.Records.push(new METoolingRowModel(this.Years, snapshot, manufacturing, op, mfgToolInvestment, this.ExchangeRates, this.Currency));
                })
            })
        });
    }

    configureHeader() {
        let header = [
            ColumnHeaders.OPTION_NAME,
            ColumnHeaders.PLANT,
            ColumnHeaders.MFG_PRO_NAME,
            ColumnHeaders.OPERATION_NUMBER,
            ColumnHeaders.OPERATION_DESCRIPTION,
            ColumnHeaders.TOOLING_INVESTMENT_DESCRIPTION,
            ColumnHeaders.TOOLING_INVESTMENT_QTY,
            ColumnHeaders.TOOLING_INVESTMENT_IS_REBILLABLE,
            ColumnHeaders.TOOLING_INVESTMENT_ELECTED_REBILL_PERCENT
        ];

        this.Years.forEach(year => {
            header.push(year + " Cost");
        });

        header.push(
            ColumnHeaders.START_YEAR,
            ColumnHeaders.CURRENCY_PREFIX
        )

        this.Years.forEach(year => {
            header.push(`${year} Cost ${ColumnHeaders.CONVERTED_PREFIX}`);
        });

        header.push(
            ColumnHeaders.TOTAL_TOOLING_INVESTMENT_COST,
            ColumnHeaders.START_YEAR,
            ColumnHeaders.CURRENCY_PREFIX,
            ColumnHeaders.TOOLING_INVESTMENT_TOOL_LIFE_PCS,
            ColumnHeaders.TOOLING_INVESTMENT_TOOL_REPLACEMENT_COST,
            `${ColumnHeaders.TOOLING_INVESTMENT_TOOL_REPLACEMENT_COST} ${ColumnHeaders.CURRENCY_PREFIX}`,
            `${ColumnHeaders.TOOLING_INVESTMENT_TOOL_REPLACEMENT_COST} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.TOOLING_INVESTMENT_TOOL_REPLACEMENT_COST} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
            ColumnHeaders.LEAD_TIME_WEEKS
        )

        this.Headers = header;
    }

    getDisplay() {
        let display = [];

        display.push(this.Headers);

        this.Records.forEach(record => {
            let row = [
                record.OptionName,
                record.Plant,
                record.MfgProName,
                record.OperationNumber,
                record.OperationDescription,
                record.ToolingInvestmentDescription,
                record.ToolingInvestmentQuantity,
                record.ToolingInvestmentIsRebillable,
                record.ToolingInvestmentElectedRebillPercent,
            ];

            record.Cost.forEach(cost => {
                row.push(cost);
            });

            row.push(
                this.Years[0],
                record.Currency
            )

            record.CostConverted.forEach(cost => {
                row.push(cost);
            });

            var total = record.CostConvertedTotal;

            row.push(
                Number.isNaN(total) ? "" : total, //Total Cost (Converted)
                this.Years[0],
                this.Currency, // Currency (Converted)
                record.ToolingInvestmnentToolLifePcs,
                record.ToolingInvestmentToolReplacementCost,
                record.ToolingInvestmentToolReplacementCostCurrency,
                record.ToolingInvestmentToolReplacementCostConv,
                record.ToolingInvestmentToolReplacementCostConvCurrency,
                record.LeadTimeWeeks
            )


            display.push(row);
        });

        return display;
    }

    getNumerics() {
        let numeric = [];

        //set number formatting
        let startCost = 8;
        let endCost = this.Years.length + startCost;
        let startConvCost = endCost + 3;
        let endConvCost = this.Years.length + startConvCost;

        numeric.push({ start: startCost, end: endCost });
        numeric.push({ start: startConvCost, end: endConvCost });

        return numeric;
    }
}

export class METoolingRowModel {
    OptionName = "";
    Plant = "";
    MfgProName = "";
    OperationNumber = "";
    OperationDescription = "";
    ToolingInvestmentDescription = "";
    ToolingInvestmentQuantity = "";
    ToolingInvestmentIsRebillable = "";
    ToolingInvestmentElectedRebillPercent = "";
    ToolingInvestmnentToolLifePcs = "";
    ToolingInvestmentToolReplacementCost = "";
    ToolingInvestmentToolReplacementCostCurrency = "";
    ToolingInvestmentToolRelpacementCostConv = "";
    ToolingInvestmentToolReplacementCostConvCurrency = ""
    LeadTimeWeeks = "";
    Cost = [];
    Currency = "";

    constructor(years, snapshot, manufacturing, operation, mfgToolInvestment, exchangeRates, exchangeCurreny) {

        this.OptionName = snapshot.GroupName;
        this.Plant = manufacturing?.Plant?.Value?.Fields[0]?.Number?.Fields[0];
        this.MfgProName = manufacturing?.Name?.Value?.Fields[0];
        this.OperationNumber = operation?.OperationNumber?.Fields[0];
        this.OperationDescription = operation?.Description?.Value?.Fields[0];
        this.ToolingInvestmentDescription = mfgToolInvestment?.Description?.Value?.Fields[0];
        this.ToolingInvestmentQuantity = mfgToolInvestment?.Quantity?.Value?.Fields[0];
        this.ToolingInvestmentIsRebillable = mfgToolInvestment?.IsRebillable?.Value?.Fields[0];
        this.ToolingInvestmentElectedRebillPercent = mfgToolInvestment?.ElectedRebillPercent?.Value?.Fields[0].Values[0]?.Value;
        this.ToolingInvestmnentToolLifePcs = mfgToolInvestment?.ToolLife?.Value?.Fields[0];
        this.ToolingInvestmentToolReplacementCost = mfgToolInvestment?.ToolReplacementCost?.Value?.Fields[0];
        this.ToolingInvestmentToolReplacementCostCurrency = mfgToolInvestment?.ToolReplacementCurrency?.Value?.Fields[0];
        this.LeadTimeWeeks = operation?.LeadTime?.Value?.Fields[0];
        this.Cost = [];
        this.CostConverted = [];
        this.CostConvertedTotal = 0;
        this.Currency = mfgToolInvestment?.Cost?.Value?.Fields[0]?.Tag;

        if (this.Currency === '') {
            this.Currency = Currency.USD;
        }

        let spendTiming = operation?.SpendTiming?.Value?.Fields[0]?.Values;

        years.forEach(year => {
            let cost = undefined;
            let convCost = undefined;

            if (mfgToolInvestment) {
                let spendTimingYear = spendTiming.find(x => new Date(x.Date).getUTCFullYear() === year);
                let mfgToolInvestmentTotalCostValues = mfgToolInvestment?.Cost?.Value?.Fields[0]?.Values?.map(item => item.Value);
                let mfgToolInvestmentTotalCost = mfgToolInvestmentTotalCostValues?.length > 0 ? mfgToolInvestmentTotalCostValues?.reduce((prev, next) => prev + next) : 0;
                cost = mfgToolInvestmentTotalCost * (spendTimingYear?.Value ?? 0);

                let exchangeRate = exchangeRates?.find(rate => rate.year === year && rate.currency === exchangeCurreny);
                let currentCurrencyRate = exchangeRates?.find(rate => rate.year === year && rate.currency === this.Currency);

                if (exchangeRate === undefined || cost === undefined || currentCurrencyRate === undefined) {
                    if (cost !== undefined) {
                        this.CostConverted.push("Error");
                    } else {
                        this.CostConverted.push("");
                    }
                } else {
                    convCost = cost * exchangeRate.value / currentCurrencyRate.value;
                    this.CostConvertedTotal += convCost;
                }
            }

            this.Cost.push(cost);
            this.CostConverted.push(convCost);
        });

        if (mfgToolInvestment && Number.isNaN(this.ToolingInvestmentToolReplacementCost) === false && this.ToolingInvestmentToolReplacementCost) {
            let firstYear = years[0];
            let firstExchangeRate = exchangeRates?.find(rate => rate.year === firstYear && rate.currency === exchangeCurreny);
            let currentCurrencyRate = exchangeRates?.find(rate => rate.year === firstYear && rate.currency === this.ToolingInvestmentToolReplacementCostCurrency);

            this.ToolingInvestmentToolReplacementCostConv = this.ToolingInvestmentToolReplacementCost * firstExchangeRate.value / currentCurrencyRate.value;
            this.ToolingInvestmentToolReplacementCostConvCurrency = exchangeCurreny;
        }

        this.CostConvertedTotal = this.CostConvertedTotal * this.ToolingInvestmentQuantity;

    }
}